import { ReactElement, useMemo } from 'react';
import { Card, Col, Descriptions, Flex, Row, Space, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { LenderDetailsProps } from '../interfaces';
import { getComplianceItems } from '../utils';
import { DescriptionsItemType } from 'antd/es/descriptions';
import styles from '../styles.module.css';

export const LenderDetails = ({ details, isFetching }: LenderDetailsProps): ReactElement => {
  const { t } = useTranslation();

  const firmComplianceItems: DescriptionsItemType[] = useMemo(() => {
    return getComplianceItems(details?.complianceCheckConfiguration?.firmPermissionLevelPerStatus);
  }, [details]);

  const intermediaryComplianceItems: DescriptionsItemType[] = useMemo(() => {
    return getComplianceItems(details?.complianceCheckConfiguration?.intermediaryPermissionLevelPerStatus);
  }, [details]);

  if (isFetching || !details) {
    return (
      <Flex justify="center" align="center" className={styles.loadingContainer}>
        <Spin />
      </Flex>
    );
  }

  return (
    <Flex vertical className={styles.scrollableContent}>
      <Flex vertical gap={16}>
        <Row gutter={16} style={{ margin: 0 }}>
          <Col span={12} className={styles.col}>
            <Card title={t('lender.lender.labels.firmComplianceConfiguration')}>
              <Space direction="vertical" size="large">
                <Descriptions column={1} size="small" layout="vertical">
                  <Descriptions.Item label={t('lender.lender.labels.initialComplianceStatus')}>
                    {t(`lender.lender.labels.${details?.complianceCheckConfiguration.defaultFirmStatus ?? ''}`)}
                  </Descriptions.Item>
                </Descriptions>
                <Space direction="vertical" size="small">
                  <Typography.Text type="secondary" strong>
                    {t('lender.lender.labels.complianceStatusPermissions')}:
                  </Typography.Text>
                  <Descriptions column={5} size="small" items={firmComplianceItems} layout="vertical" />
                </Space>
              </Space>
            </Card>
          </Col>
          <Col span={12} className={styles.col}>
            <Card title={t('lender.lender.labels.intermediaryComplianceConfiguration')}>
              <Space direction="vertical" size="large">
                <Descriptions column={1} size="small" layout="vertical">
                  <Descriptions.Item label={t('lender.lender.labels.initialComplianceStatus')}>
                    {t(`lender.lender.labels.${details?.complianceCheckConfiguration.defaultIntermediaryStatus ?? ''}`)}
                  </Descriptions.Item>
                </Descriptions>
                <Space direction="vertical" size="small">
                  <Typography.Text type="secondary" strong>
                    {t('lender.lender.labels.complianceStatusPermissions')}:
                  </Typography.Text>
                  <Descriptions column={5} size="small" items={intermediaryComplianceItems} layout="vertical" />
                </Space>
              </Space>
            </Card>
          </Col>
        </Row>
      </Flex>
    </Flex>
  );
};
